import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, Twitter, Linkedin, Mail, Phone, MapPin, Navigation, Star, HeartHandshake } from "lucide-react";
import { api } from "@/lib/api";
import { BrandLogo } from "./BrandLogo";
import { WhatsAppIcon, GoogleIcon } from "./BrandIcons";

export default function Footer() {
  const [copy, setCopy] = useState({});
  const [donationsOn, setDonationsOn] = useState(false);
  useEffect(() => {
    api.get("/homepage").then((r) => setCopy(r.data || {})).catch(() => {});
    api.get("/payment-status").then((r) => setDonationsOn(!!r.data?.donations_enabled)).catch(() => {});
  }, []);
  const c = copy.contact || {};
  const s = c.socials || {};

  // Google Maps deep-link: prefer explicit link, else fall back to a search of the address.
  const directionsHref = c.google_maps_url
    || (c.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent("HTP Sports Hub " + c.address)}` : null);

  // WhatsApp deep link — wa.me handles both raw phones and full URLs.
  const whatsappHref = s.whatsapp
    ? (s.whatsapp.startsWith("http") ? s.whatsapp : `https://wa.me/${s.whatsapp.replace(/[^0-9]/g, "")}`)
    : null;

  const socials = [
    { key: "instagram", url: s.instagram, Icon: Instagram, label: "Instagram" },
    { key: "facebook",  url: s.facebook,  Icon: Facebook,  label: "Facebook" },
    { key: "youtube",   url: s.youtube,   Icon: Youtube,   label: "YouTube" },
    { key: "twitter",   url: s.twitter,   Icon: Twitter,   label: "Twitter / X" },
    { key: "linkedin",  url: s.linkedin,  Icon: Linkedin,  label: "LinkedIn" },
  ].filter((x) => x.url);

  return (
    <footer className="border-t border-white/12 bg-[#0B1120] mt-24" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <BrandLogo as="div" size="lg" showWordmark={false} />
            <span className="font-display text-xl font-black tracking-tight uppercase">HTP Sports Hub<span className="brand-orange">.</span></span>
          </div>
          <p className="text-white/60 text-sm leading-relaxed">
            An integrated sports complex for elite athletes, weekend warriors and future champions.
          </p>

          {/* Google + Directions quick actions */}
          <div className="mt-5 flex flex-wrap gap-2">
            {c.google_business_url && (
              <a
                href={c.google_business_url}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-google-page"
                className="inline-flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase tracking-widest border border-white/20 bg-white text-[#0B1120] hover:bg-white/90"
                title="Visit our Google page"
              >
                <GoogleIcon size={14} /> Google Page
              </a>
            )}
            {directionsHref && (
              <a
                href={directionsHref}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-directions"
                className="inline-flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase tracking-widest border border-brand-blue/60 text-brand-blue hover:bg-brand-blue hover:text-white transition"
                title="Open directions on Google Maps"
              >
                <Navigation size={13} /> Directions
              </a>
            )}
            {c.google_business_url && (
              <a
                href={c.google_business_url}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-google-review"
                className="inline-flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase tracking-widest border border-brand-orange/50 text-brand-orange hover:bg-brand-orange hover:text-black transition"
                title="Leave a Google review"
              >
                <Star size={13} /> Review
              </a>
            )}
          </div>
        </div>
        <div>
          <h4 className="font-mono text-xs uppercase tracking-widest text-white/40 mb-4">Explore</h4>
          <ul className="space-y-2 text-sm text-white/70">
            <li><Link to="/booking" className="hover:text-white">Book a slot</Link></li>
            <li><Link to="/tournaments" className="hover:text-white">Tournaments</Link></li>
            <li><Link to="/team" className="hover:text-white">About Us</Link></li>
            <li><Link to="/gallery" className="hover:text-white">Gallery</Link></li>
            <li><Link to="/blogs" className="hover:text-white">Journal</Link></li>
            <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-mono text-xs uppercase tracking-widest text-white/40 mb-4">Contact</h4>
          <ul className="space-y-2 text-sm text-white/70">
            <li className="flex items-start gap-2"><MapPin size={14} className="mt-1 brand-orange shrink-0" /> {c.address || "Sector 42, Sports District"}</li>
            <li className="flex items-center gap-2"><Phone size={14} className="brand-orange shrink-0" /> {c.phone || "+91 98123 45678"}</li>
            <li className="flex items-center gap-2"><Mail size={14} className="brand-orange shrink-0" /> {c.email || "hello@htpsportshub.com"}</li>
          </ul>
          {whatsappHref && (
            <a
              href={whatsappHref}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-whatsapp"
              className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 bg-[#25D366] text-black font-bold text-xs uppercase tracking-widest hover:opacity-90 transition"
              title="Chat with us on WhatsApp"
            >
              <WhatsAppIcon size={16} /> WhatsApp Us
            </a>
          )}
        </div>
        <div>
          <h4 className="font-mono text-xs uppercase tracking-widest text-white/40 mb-4">Follow us</h4>
          <div className="flex flex-wrap gap-3 text-white/60">
            {socials.length > 0 ? socials.map(({ key, url, Icon, label }) => (
              <a
                key={key}
                href={url}
                target="_blank"
                rel="noreferrer"
                aria-label={label}
                data-testid={`footer-social-${key}`}
                className="w-9 h-9 border border-white/20 hover:border-brand-blue hover:text-brand-blue hover:bg-white/5 flex items-center justify-center transition"
              >
                <Icon size={16} />
              </a>
            )) : <span className="text-xs text-white/40">Social links coming soon</span>}
          </div>
        </div>
      </div>
      <div className="border-t border-white/12 py-5 text-center text-xs font-mono uppercase tracking-widest text-white/40">
        © {new Date().getFullYear()} HTP Sports Hub · Train Like A Pro
      </div>
    </footer>
  );
}
